import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { EMPLOYEES_WITH_STATS } from '@/data/employees';
import type { EmployeeWithStats } from '@/data/employees';
import {
  DASHBOARD_PROJECT_ROWS,
  DASHBOARD_PROJECT_TOTALS,
  DASHBOARD_SYNC,
} from '@/data/dashboardProjects';
import type { DashboardProjectRow, DashboardProjectTotals } from '@/data/dashboardProjects';
import { DASHBOARD_DAILY_STATS } from '@/data/dashboardDailyStats';
import type { DailyStat } from '@/data/dashboardDailyStats';
import type {
  EmployeeStatsResponse,
  ProjectSummaryResponse,
  SyncStatusResponse,
} from '@/types/dashboard';

interface UseDashboardEmployeesResult {
  data: readonly EmployeeWithStats[];
  isLoading: boolean;
}

export function useDashboardEmployees(): UseDashboardEmployeesResult {
  const { mockupMode, currentOrg, dateRange } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const query = useQuery({
    queryKey: ['dashboardEmployees', orgId, dateRange],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams();
      if (dateRange.start) qs.set('startDate', dateRange.start);
      if (dateRange.end) qs.set('endDate', dateRange.end);
      const qsStr = qs.toString();
      const res = await apiFetch<EmployeeStatsResponse>(
        `/api/v1/orgs/${orgId}/dashboard/employees/stats${qsStr ? `?${qsStr}` : ''}`,
        token,
      );
      return res.employees.map(
        (e): EmployeeWithStats => ({
          id: e.id,
          name: e.name,
          role: 'member',
          businessUnitId: '',
          email: '',
          totalHours: e.totalHours,
          // Task 145 D2 — pull real billable/general/eventCount values from
          // the response instead of hardcoding zero. The endpoint started
          // surfacing them in task 145 D1.
          billableHours: e.billableHours,
          generalHours: e.generalHours,
          events: e.eventCount,
          // Task 113 D4 — round to whole percent for the member card
          // "% billable" label. The rounded value is also fine for the
          // progress-bar width (visually indistinguishable from float).
          billablePercent:
            e.avgBillablePerDay > 0
              ? Math.round((e.avgBillablePerDay / e.avgHoursPerDay) * 100)
              : 0,
          fullDays: e.fullDays,
          halfDays: e.halfDays,
          checkIns: e.checkIns,
          effectiveDays: e.effectiveDays,
          avgHoursDay: e.avgHoursPerDay,
          avgBillableDay: e.avgBillablePerDay,
        }),
      );
    },
    enabled: !mockupMode && !!orgId,
  });

  if (mockupMode) return { data: EMPLOYEES_WITH_STATS, isLoading: false };
  return { data: query.data ?? [], isLoading: query.isLoading };
}

/**
 * One client group in the dashboard's Clients & Projects accordion (task 145).
 *
 * - `clientId === null` is the Internal / Not Set bucket (rows whose project
 *   has no client). The reserved-Internal slug is task 131's territory; this
 *   shape just preserves the null grouping.
 * - `rollup.pct` is `rollup.hours / totals.hours`, so it sums to 100 across
 *   the visible groups (modulo rounding).
 */
export interface DashboardClientGroup {
  clientId: string | null;
  clientName: string;
  projects: DashboardProjectRow[];
  rollup: { hours: number; pct: number; events: number };
}

interface UseDashboardProjectsResult {
  data: readonly DashboardClientGroup[];
  totals: DashboardProjectTotals;
  isLoading: boolean;
}

/**
 * Group a flat list of project rows by client. Mockup mode reuses
 * `DASHBOARD_PROJECT_ROWS` (task 145 §Mockup-mode parity opted for "group at
 * hook time, no parallel fixture").
 */
function groupRowsByClient(
  rows: readonly DashboardProjectRow[],
  totalHours: number,
): DashboardClientGroup[] {
  // Use a Map so insertion order is preserved — the source rows are already
  // sorted by hours desc, so the first appearance of a client is its
  // highest-hour project.
  const groups = new Map<string, DashboardClientGroup>();
  for (const row of rows) {
    // null client → the Internal / Not Set bucket. Use a distinct synthetic
    // key so the Map can still group without colliding with a literal client
    // name — see task 131 for the reserved Internal client semantics.
    const key = row.client ?? '__internal_not_set__';
    const existing = groups.get(key);
    if (existing) {
      existing.projects.push(row);
      existing.rollup.hours += row.hours;
      existing.rollup.events += row.events;
    } else {
      groups.set(key, {
        clientId: row.client ?? null,
        clientName: row.client ?? 'Internal / Not Set',
        projects: [row],
        rollup: { hours: row.hours, events: row.events, pct: 0 },
      });
    }
  }
  // Stamp the rollup pct now that hours are summed.
  for (const g of groups.values()) {
    g.rollup.pct = totalHours > 0 ? (g.rollup.hours / totalHours) * 100 : 0;
  }
  return Array.from(groups.values());
}

export function useDashboardProjects(): UseDashboardProjectsResult {
  const { mockupMode, currentOrg, dateRange } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const query = useQuery({
    queryKey: ['dashboardProjects', orgId, dateRange],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams();
      if (dateRange.start) qs.set('startDate', dateRange.start);
      if (dateRange.end) qs.set('endDate', dateRange.end);
      const qsStr = qs.toString();
      const res = await apiFetch<ProjectSummaryResponse>(
        `/api/v1/orgs/${orgId}/dashboard/projects/summary${qsStr ? `?${qsStr}` : ''}`,
        token,
      );
      const totalHours = res.projects.reduce((sum, p) => sum + p.totalHours, 0);
      const totalEvents = res.projects.reduce((sum, p) => sum + p.eventCount, 0);
      const rows: DashboardProjectRow[] = res.projects.map((p) => ({
        name: p.projectName,
        client: p.clientName || null,
        type: p.billable ? ('billable' as const) : ('general' as const),
        hours: p.totalHours,
        pct: totalHours > 0 ? (p.totalHours / totalHours) * 100 : 0,
        events: p.eventCount,
      }));
      const groups = groupRowsByClient(rows, totalHours);
      return { groups, totals: { hours: totalHours, events: totalEvents } };
    },
    enabled: !mockupMode && !!orgId,
  });

  if (mockupMode) {
    // Task 145 §Mockup-mode parity — group the existing flat fixture instead
    // of adding a parallel grouped fixture.
    const groups = groupRowsByClient(DASHBOARD_PROJECT_ROWS, DASHBOARD_PROJECT_TOTALS.hours);
    return { data: groups, totals: DASHBOARD_PROJECT_TOTALS, isLoading: false };
  }
  return {
    data: query.data?.groups ?? [],
    totals: query.data?.totals ?? { hours: 0, events: 0 },
    isLoading: query.isLoading,
  };
}

interface UseDashboardSyncResult {
  data: { status: string; lastSyncRelative: string };
  isLoading: boolean;
}

export function useDashboardSync(): UseDashboardSyncResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const query = useQuery({
    queryKey: ['dashboardSync', orgId],
    queryFn: async () => {
      const token = await getToken();
      const res = await apiFetch<SyncStatusResponse>(
        `/api/v1/orgs/${orgId}/dashboard/sync/status`,
        token,
      );
      const lastTick = res.workerHeartbeat?.lastTickAt;
      let lastSyncRelative = '';
      if (lastTick) {
        // eslint-disable-next-line no-restricted-syntax -- live API response needs real wall-clock diff, not frozen time
        const diffMs = Date.now() - new Date(lastTick).getTime();
        const diffMin = Math.round(diffMs / 60000);
        lastSyncRelative = diffMin < 1 ? 'just now' : `${diffMin}m ago`;
      }
      return {
        status: res.workerHeartbeat?.stale ? 'Stale' : 'Active',
        lastSyncRelative,
      };
    },
    enabled: !mockupMode && !!orgId,
    refetchInterval: 30_000,
  });

  if (mockupMode) return { data: DASHBOARD_SYNC, isLoading: false };
  return {
    data: query.data ?? { status: 'Unknown', lastSyncRelative: '' },
    isLoading: query.isLoading,
  };
}

// ── Task 157: org-wide daily-stats hook ──────────────────────────────────
// Surfaces the data the dashboard time-series chart consumes (billable vs
// non-billable hours per day across the active date range). In mockup mode
// it returns DASHBOARD_DAILY_STATS clipped to the active range. In live
// mode there is no backing endpoint yet — task 157 §Out of scope blocks
// new backend work — so the hook returns an empty array, and the chart
// falls back to EmptyState. The chart's component-level test covers both
// branches.

interface UseDashboardDailyStatsResult {
  data: readonly DailyStat[];
  isLoading: boolean;
}

function clipDailyStatsToRange(
  stats: readonly DailyStat[],
  start: string,
  end: string,
): readonly DailyStat[] {
  if (!start && !end) return stats;
  return stats.filter((d) => {
    if (start && d.date < start) return false;
    if (end && d.date > end) return false;
    return true;
  });
}

export function useDashboardDailyStats(): UseDashboardDailyStatsResult {
  const { mockupMode, dateRange } = useAppContext();

  if (mockupMode) {
    return {
      data: clipDailyStatsToRange(DASHBOARD_DAILY_STATS, dateRange.start, dateRange.end),
      isLoading: false,
    };
  }
  // Live mode: endpoint is not implemented (task 157 defers backend work).
  // The chart respects this by rendering EmptyState when the array is
  // empty. When the endpoint lands, this branch will use react-query
  // against `/api/v1/orgs/:orgId/dashboard/daily-stats`.
  return { data: [], isLoading: false };
}
